import { Injectable } from '@angular/core';

export const LOCAL_STORAGE_KEYS = {
  CREDIT_REPORT_ANSWERS: 'credit_report_answers',
  OPEN_BANKING_FLOW: 'ob-flow',
  POST_LOGOUT: 'msal.postLogout',
  ANALYTICS_CONSENT: 'analytics_consent',
} as const;
export type LocalStorageKey = (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /**
   * Sets a localStorage item by key
   * @param key - The key of the item to set
   * @param value - The item value to set
   */
  setItem(key: LocalStorageKey, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to set localStorage item for key: ${key}`, error);
    }
  }

  /**
   * Gets a localStorage item by key
   * @param key - The key of the item to retrieve
   * @returns The item value or null if not found
   */
  getItem(key: LocalStorageKey): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to get localStorage item for key: ${key}`, error);
      return null;
    }
  }

  /**
   * Removes a localStorage item by key
   * @param key - The key of the item to remove
   */
  removeItem(key: LocalStorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove localStorage item for key: ${key}`, error);
    }
  }

  /**
   * Sets a localStorage item by key
   * @param key - The key of the item to set
   * @param value - The item value to set
   */
  setJSON<T>(key: LocalStorageKey, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      this.setItem(key, serialized);
    } catch (error) {
      console.error(`Failed to serialize and set JSON for key: ${key}`, error);
    }
  }

  /**
   * Gets a localStorage item by key
   * @param key - The key of the item to retrieve
   * @returns The item value or null if not found
   */
  getJSON<T>(key: LocalStorageKey): T | null {
    try {
      const raw = this.getItem(key);
      if (raw == null) return null;

      return JSON.parse(raw) as T;
    } catch (error) {
      console.error(`Failed to parse JSON from localStorage for key: ${key}`, error);
      return null;
    }
  }
}
